/**
 * The idle page's first question: is there anything to release?
 *
 * It is answered by `main/unreleased`, which the system writes at every
 * `host.sync` — so this card is also the place the answer gets refreshed. That
 * sequence (check, then decide) is the whole idle flow; the start-a-release card
 * only appears below once the answer is yes. The card is reached only with the
 * system awake — `HomePage` shows the sleeping panel otherwise — so the check is
 * always something that can run now.
 *
 * It is drawn as the page's hero — one large answer with an icon whose colour
 * carries the verdict — because everything else on the idle page is in service
 * of this sentence. The four honest answers (`lib/unreleased.ts`) get four
 * distinct treatments so that "unknown" cannot be misread as "nothing" at a
 * glance: an unanswered question looks like a question, not like a clean bill.
 */
import type { ReactElement } from 'react';
import {
    Alert,
    AlertTitle,
    Avatar,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import RocketLaunchOutlined from '@mui/icons-material/RocketLaunchOutlined';
import { INSTRUCTION_KIND, type UnreleasedComponent } from '@finvari/deployer';
import { useHost, useInstructionAction, useNow, useUnreleased, CAPTION_TICK_MS } from '../hooks';
import { unreleasedView, type UnreleasedState } from '../lib/unreleased';
import { ActivityAlert } from './ActivityAlert';

type Tone = 'info' | 'warning' | 'success' | 'primary';

/**
 * The verdict's colour and icon, by what the last check found. `unchecked` is
 * deliberately neutral rather than green or red: no answer is not an answer.
 */
const STATE_TONE: Record<UnreleasedState, { tone: Tone; icon: ReactElement }> = {
    unchecked: { tone: 'info', icon: <HelpOutlineRounded /> },
    stale: { tone: 'warning', icon: <HistoryRounded /> },
    clean: { tone: 'success', icon: <CheckCircleOutlineRounded /> },
    changes: { tone: 'primary', icon: <RocketLaunchOutlined /> },
};

/** Short sha for a caption. */
const SHA_CHARS = 8;

/** Why a component is in the list, for the chip's tooltip. */
function componentTooltip(changeKind: UnreleasedComponent['changeKind'], fileCount: number): string {
    switch (changeKind) {
        case 'direct':
            return `${fileCount} of its own files changed.`;
        case 'compile-only':
            return 'Rebuilt because a package it depends on changed, but nothing it ships reaches a changed file at runtime — expect no deploy.';
        default:
            return 'None of its own files changed; it is rebuilt because a package it depends on did.';
    }
}

/** How a component chip reads, by why it is in the list. */
function componentLabel(c: UnreleasedComponent): string {
    if (c.changeKind === 'direct') return `${c.name} · ${c.fileCount}`;
    return c.changeKind === 'compile-only' ? `${c.name} · rebuild only` : `${c.name} · via a dependency`;
}

export function UnreleasedCard() {
    const { value: doc } = useUnreleased();
    const host = useHost('prepare');
    const now = useNow(CAPTION_TICK_MS);
    const view = unreleasedView(doc, host.value, now);
    const { tone, icon } = STATE_TONE[view.state];

    const check = useInstructionAction('prepare', INSTRUCTION_KIND.hostSync);

    // Checking is the primary action only when it is the one thing left to do:
    // starting a release outranks it once there is something to release.
    const primary = view.state !== 'changes';
    const checkButton = (
        <Button
            variant={primary ? 'contained' : 'outlined'}
            onClick={check.run}
            disabled={check.sending || !!check.disabledReason}
            startIcon={check.sending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
            {check.sending ? 'Sending…' : 'Check for changes'}
        </Button>
    );

    // What the answer was measured against. Naming the two shas lets an operator
    // who is looking at git (or at the host card's checkout line) confirm the
    // check is about the branches they think it is.
    const provenance = doc
        ? `Compared origin/main ${doc.mainSha.slice(0, SHA_CHARS)} with release ${doc.releaseSha.slice(0, SHA_CHARS)} — recomputed at every sync.`
        : 'The system answers this when it syncs its checkout; the answer is only as current as the last check.';

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Avatar
                        sx={{
                            width: 48,
                            height: 48,
                            color: `${tone}.main`,
                            bgcolor: (theme) => alpha(theme.palette[tone].main, 0.12),
                        }}
                    >
                        {icon}
                    </Avatar>
                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                            Unreleased changes
                        </Typography>
                        <Typography variant="h5" component="h2">
                            {view.headline}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {view.detail}
                        </Typography>
                        {view.components.length > 0 && (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
                                {view.components.map((c) => (
                                    <Tooltip key={c.name} title={componentTooltip(c.changeKind, c.fileCount)}>
                                        <Chip
                                            size="small"
                                            variant={c.changeKind === 'direct' ? 'filled' : 'outlined'}
                                            // Dimmed, because this chip is saying the
                                            // release will not have to deploy it.
                                            sx={c.changeKind === 'compile-only' ? { opacity: 0.6 } : undefined}
                                            label={componentLabel(c)}
                                        />
                                    </Tooltip>
                                ))}
                            </Stack>
                        )}
                        {/*
                          * A connector build or an app version bump in the waiting
                          * work changes what the release will ask of the operator —
                          * an extra announcement to review, and for the connector an
                          * installer to deploy before it can be posted. Said here
                          * rather than discovered at the review gate.
                          */}
                        {view.componentReleases.length > 0 && (
                            <Alert severity="info" sx={{ mt: 0.5 }}>
                                <AlertTitle>
                                    Releasing this would also release{' '}
                                    {view.componentReleases.length === 1 ? 'a component' : 'components'}
                                </AlertTitle>
                                <Stack spacing={0.5}>
                                    {view.componentReleases.map((line) => (
                                        <Typography key={line} variant="body2">
                                            {line}
                                        </Typography>
                                    ))}
                                </Stack>
                            </Alert>
                        )}
                        {/*
                          * Work the release will create and cannot do: no deploy in it
                          * applies a Pulumi program, so each of these ends in a
                          * `pulumi up` by an owner and holds the close until somebody
                          * says it happened. Worth knowing before the release starts,
                          * because arranging that is easier than discovering at the
                          * close that it was needed.
                          */}
                        {view.infraPrograms.length > 0 && (
                            <Alert severity="warning" sx={{ mt: 0.5 }}>
                                <AlertTitle>
                                    {view.infraPrograms.length === 1
                                        ? 'It also changes a Pulumi program'
                                        : `It also changes ${view.infraPrograms.length} Pulumi programs`}
                                </AlertTitle>
                                <Stack spacing={0.5}>
                                    {view.infraPrograms.map((line) => (
                                        <Typography key={line} variant="body2">
                                            {line}
                                        </Typography>
                                    ))}
                                </Stack>
                            </Alert>
                        )}
                        <Typography variant="caption" color="text.secondary">
                            {provenance}
                        </Typography>
                        {check.latest && check.activity && (
                            <ActivityAlert
                                host="prepare"
                                row={check.latest}
                                line={check.activity}
                                label="Last check"
                                variant="inline"
                            />
                        )}
                        {check.error && (
                            <Typography variant="body2" color="error">
                                {check.error}
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            </CardContent>
            <Divider />
            <CardActions sx={{ px: 2, py: 1.5, gap: 1 }}>
                {check.disabledReason ? (
                    <Tooltip title={check.disabledReason}>
                        <span>{checkButton}</span>
                    </Tooltip>
                ) : (
                    checkButton
                )}
            </CardActions>
        </Card>
    );
}
